import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Link2, Lock, AlertTriangle } from 'lucide-react';

export default function SharedContentPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<'loading' | 'success' | 'error' | 'password'>('loading');
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setError('Invalid share link'); return; }
    (async () => {
      try {
        const res = await fetch('/_api/user-media/share-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'access', shareToken: token }),
        });
        const json = await res.json();
        if (res.ok) {
          setData(json);
          setState('success');
        } else {
          setError(json.error || 'Link not found or expired');
          setState(json.error?.includes('password') ? 'password' : 'error');
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load');
        setState('error');
      }
    })();
  }, [token]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <nav style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#fff' }}>
          <img src="/logo.png" alt="Tasking.tech" style={{ width: 28, height: 28 }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>tasking<span style={{ color: '#f97316' }}>.tech</span></span>
        </Link>
        <Link to="/login" style={{ color: '#999', textDecoration: 'none', fontSize: '0.85rem' }}>Log in</Link>
      </nav>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        {state === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#f97316' }} />
            <p style={{ color: '#999' }}>Loading shared content...</p>
          </div>
        )}

        {state === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={28} style={{ color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Link unavailable</h2>
            <p style={{ color: '#999', maxWidth: 400, margin: 0 }}>{error}</p>
            <Link to="/" style={{ color: '#f97316', textDecoration: 'none', fontSize: '0.85rem' }}>Go to Tasking.tech</Link>
          </div>
        )}

        {state === 'success' && data && (
          <div style={{ maxWidth: 900, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              <Link2 size={18} style={{ color: '#f97316' }} />
              <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Shared Content</h1>
            </div>

            {data.asset?.fileType?.startsWith('image/') && (
              <img
                src={data.asset.url}
                alt={data.asset.fileName}
                style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            )}

            {(data.asset?.fileType === 'application/pdf' || data.asset?.fileName?.endsWith('.pdf')) && (
              <iframe
                src={data.asset.url}
                title={data.asset.fileName}
                style={{ width: '100%', height: '80vh', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            )}

            {data.asset?.fileName && (
              <div style={{ marginTop: 16, padding: '16px 20px', background: '#111118', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>{data.asset.fileName}</p>
                {data.asset.fileSize && (
                  <p style={{ margin: '4px 0 0', color: '#999', fontSize: '0.8rem' }}>
                    {(data.asset.fileSize / 1024).toFixed(1)} KB
                  </p>
                )}
                {data.permission === 'download' && data.asset.url && (
                  <a
                    href={data.asset.url}
                    download={data.asset.fileName}
                    style={{ display: 'inline-block', marginTop: 12, padding: '8px 16px', background: '#f97316', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    Download
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
